<template>
    <DropdownButton
        v-model="isOpen"
        :items="dropdownItems"
        :sections="dropdownSections"
        :show-checkbox="true"
        :ref-name="refName"
        button-icon="common__slider"
        @select="onSelect"
    />
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import DropdownButton from '@/components/_base/dropdown/DropdownButton.vue'

export default {
    name: 'UnitsFilterDropdownButton',
    components: {
        DropdownButton,
    },
    data() {
        return {
            refName: 'UnitsTypes'
        }
    },
    computed: {
        ...mapGetters([
            'showFilterDropdownMenu',
            'getUnitsTypes',
        ]),
        isOpen: {
            get() {
                return this.showFilterDropdownMenu
            },
            set(value) {
                this.setShowFilterDropdownMenu(value)
            },
        },
        dropdownItems() {
            return []
        },
        dropdownSections() {
            return [
                {
                    key: 'all',
                    items: [
                        {
                            label: this.$t('Units.status.All units'),
                            value: '__reset__',
                            dataType: 'reset',
                            icon: 'common__car',
                            iconClass: '',
                            isActive: this.isAllFiltersEmpty,
                        },
                    ],
                },
                {
                    key: 'search',
                    items: [
                        {
                            label: this.$t('Units.other.Name'),
                            value: 'name',
                            dataType: 'search',
                            icon: 'common__car',
                            iconClass: '',
                            isActive: this.activeSearchValues.includes('name'),
                        },
                        {
                            label: this.$t('Units.other.Camera Type'),
                            value: 'cameraType',
                            dataType: 'search',
                            icon: 'camera_type',
                            iconClass: '',
                            isActive: this.activeSearchValues.includes('cameraType'),
                        },
                        {
                            label: this.$t('Units.other.Device Type'),
                            value: 'deviceType',
                            dataType: 'search',
                            icon: 'device_type',
                            iconClass: '',
                            isActive: this.activeSearchValues.includes('deviceType'),
                        },
                        {
                            label: this.$t('Units.other.IMEI'),
                            value: 'imei',
                            dataType: 'search',
                            icon: 'imei',
                            iconClass: '',
                            isActive: this.activeSearchValues.includes('imei'),
                        },
                        {
                            label: this.$t('Units.other.Camera ID'),
                            value: 'cameraId',
                            dataType: 'search',
                            icon: 'camera_id',
                            iconClass: '',
                            isActive: this.activeSearchValues.includes('cameraId'),
                        },
                        {
                            label: this.$t('Units.other.DMS'),
                            value: 'dms',
                            dataType: 'search',
                            icon: 'DMS',
                            iconClass: '',
                            isActive: this.activeSearchValues.includes('dms'),
                        },
                    ],
                },
                {
                    key: 'signal',
                    items: [
                        {
                            label: this.$t('Units.signals.ON line'),
                            value: 'on',
                            dataType: 'signal',
                            icon: 'common__signal',
                            iconClass: 'is_success',
                            isActive: this.routeQueryFilter.signal === 'on',
                        },
                        {
                            label: this.$t('Units.signals.OFF line'),
                            value: 'off',
                            dataType: 'signal',
                            icon: 'common__signal_lost',
                            iconClass: 'is_danger',
                            isActive: this.routeQueryFilter.signal === 'off',
                        },
                    ],
                },
                {
                    key: 'movement',
                    items: [
                        {
                            label: this.$t('Units.movements.Moving'),
                            value: 'moving',
                            dataType: 'movement',
                            icon: 'status_move',
                            iconClass: 'connection_status-online',
                            isActive: this.routeQueryFilter.movement === 'moving',
                        },
                        {
                            label: this.$t('Units.movements.Stopped'),
                            value: 'stopped',
                            dataType: 'movement',
                            icon: 'status_stop',
                            iconClass: 'connection_status_blue',
                            isActive: this.routeQueryFilter.movement === 'stopped',
                        },
                        {
                            label: this.$t('Units.movements.Ignition on'),
                            value: 'ignition',
                            dataType: 'movement',
                            icon: 'ignition-on',
                            iconClass: 'connection_status-online',
                            isActive: this.routeQueryFilter.movement === 'ignition',
                        },
                    ],
                },
                {
                    key: 'camera',
                    items: (this.$consts?.FilterSearchLists?.Units?.camera ?? []).map(camera => ({
                        label: this.$t(camera.translate),
                        value: camera.id,
                        dataType: 'camera',
                        icon: camera.icon,
                        iconClass: 'is_warning',
                        iconStyle: camera.color ? { color: camera.color } : null,
                        isActive: this.routeQueryFilter.camera === camera.id,
                    })),
                },
                {
                    key: 'cargo',
                    items: [
                        {
                            label: this.$t('legend.Loaded'),
                            value: 'loaded',
                            dataType: 'cargo',
                            icon: 'chassis_loaded',
                            iconClass: '',
                            isActive: this.routeQueryFilter.cargo === 'loaded',
                        },
                        {
                            label: this.$t('legend.Unloaded'),
                            value: 'unloaded',
                            dataType: 'cargo',
                            icon: 'chassis_unloaded',
                            iconClass: '',
                            isActive: this.routeQueryFilter.cargo === 'unloaded',
                        },
                    ],
                },
                {
                    key: 'other',
                    items: [
                        {
                            label: this.$t('Units.other.In Zone'),
                            value: 'geozone',
                            dataType: 'other',
                            icon: 'in_zone',
                            iconClass: 'is_success',
                            isActive: this.routeQueryFilter.other === 'geozone',
                        },
                        {
                            label: this.$t('Units.other.Driver Assigned'),
                            value: 'driver',
                            dataType: 'other',
                            icon: 'driver_assigned',
                            iconClass: '',
                            isActive: this.routeQueryFilter.other === 'driver',
                        },
                    ],
                },
                {
                    key: 'type',
                    items: (this.getUnitsTypes ?? [])
                        .filter(type => type.filterByTypes)
                        .map(type => ({
                            label: this.$te(`Units.types.${type.name}`)
                                ? this.$t(`Units.types.${type.name}`)
                                : type.name,
                            value: type.id,
                            dataType: 'type',
                            icon: type.icon,
                            iconClass: '',
                            isActive: this.routeQueryFilter.type === type.id,
                        })),
                },
            ].filter(section => section.items.length > 0)
        },
        activeSearchValues() {
            let value = this.routeQueryFilter.search
            if (!value) return []
            return Array.isArray(value) ? value : [value]
        },
        isAllFiltersEmpty() {
            return !this.routeQueryFilter.signal
                && !this.routeQueryFilter.movement
                && !this.routeQueryFilter.camera
                && !this.routeQueryFilter.type
                && !this.routeQueryFilter.name
                && !this.routeQueryFilter.other
                && !this.routeQueryFilter.cargo
                && !this.routeQueryFilter.search
        },
    },

    methods: {
        ...mapMutations([
            'setShowFilterDropdownMenu',
        ]),

        onSelect(item) {
            if (item.dataType === 'reset' || item.value === '__reset__') {
                this.applyRouteFilter(null)
                return
            }

            if (item.dataType === 'search') {
                this.toggleSearchFilterParam(item.value)
                return
            }

            this.setUnitsQueryFilterParam(item.dataType, item.value)
        },

        toggleSearchFilterParam(value) {
            const nextValues = this.activeSearchValues.includes(value)
                ? this.activeSearchValues.filter(v => v !== value)
                : [...this.activeSearchValues, value]

            const nextFilter = { ...this.routeQueryFilter }
            if (nextValues.length) {
                nextFilter.search = nextValues
            } else {
                delete nextFilter.search
            }

            this.applyRouteFilter(nextFilter)
        },

        setUnitsQueryFilterParam(param, value) {
            const currentValue = this.routeQueryFilter[param]

            const nextFilter = { ...this.routeQueryFilter }

            // 👉 toggle логика
            if (currentValue === value) {
                delete nextFilter[param]
            } else {
                nextFilter[param] = value
            }

            this.applyRouteFilter(nextFilter)
        },

        applyRouteFilter(filter) {
            const normalizedNext = this.normalizeFilter(filter)
            const normalizedCurrent = this.normalizeFilter(this.routeQueryFilter)

            if (this.isSameFilter(normalizedCurrent, normalizedNext)) {
                return
            }

            const hasFilters = Object.keys(normalizedNext).length > 0
            this.setRoute.unitsFilter(
                this.$route,
                this.$router,
                hasFilters ? normalizedNext : null
            )
        },

        normalizeFilter(filter) {
            if (!filter || typeof filter !== 'object') {
                return {}
            }

            return Object.keys(filter)
                .sort()
                .reduce((acc, key) => {
                    const value = filter[key]

                    if (value !== undefined && value !== null && value !== '') {
                        acc[key] = value
                    }

                    return acc
                }, {})
        },

        isSameFilter(a, b) {
            const aKeys = Object.keys(a)
            const bKeys = Object.keys(b)

            if (aKeys.length !== bKeys.length) {
                return false
            }

            return aKeys.every(key => a[key] === b[key])
        },
    },
}
</script>

<style scoped lang="scss">

</style>